import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ProfessionalCard } from '@/components/ProfessionalCard';
import { CaregiverFilters } from '@/components/filters/CaregiverFilters';
import { CaregiverFilters as FilterType } from '@/types/caregiver';

import { ExtendedDirectoryEntry } from '@/types/caregiver';

type DirectoryEntry = ExtendedDirectoryEntry;

interface UnifiedProfessionalDirectoryProps {
  professionType?: string;
  title?: string;
  description?: string;
}

const professionTypes = [
  { value: 'all', label: 'All Professionals', count: null },
  { value: 'Geriatric Care Manager', label: 'Geriatric Care Managers', count: 1043 },
  { value: 'Other', label: 'Other Senior Care Professionals', count: 506 },
  { value: 'Occupational Therapist', label: 'Occupational Therapists', count: 165 },
  { value: 'Physical Therapist', label: 'Physical Therapists', count: 128 },
  { value: 'Social Worker', label: 'Social Workers', count: 90 },
  { value: 'Financial Advisor', label: 'Financial Advisors', count: 28 },
  { value: 'Home Care Agency', label: 'Home Care Agencies', count: 26 },
  { value: 'Elder Law Attorney', label: 'Elder Law Attorneys', count: 23 },
];

const professionDisplayNames: Record<string, string> = {
  'Geriatric Care Manager': 'Geriatric Care Manager',
  'Other': 'Other Senior Care Professional',
  'Occupational Therapist': 'Occupational Therapist',
  'Physical Therapist': 'Physical Therapist',
  'Social Worker': 'Social Worker',
  'Financial Advisor': 'Financial Advisor',
  'Home Care Agency': 'Home Care Agency',
  'Elder Law Attorney': 'Elder Law Attorney',
};

export function UnifiedProfessionalDirectory({ 
  professionType = 'all',
  title,
  description 
}: UnifiedProfessionalDirectoryProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [selectedProfession, setSelectedProfession] = useState(
    searchParams.get('type') || professionType
  );
  const [entries, setEntries] = useState<DirectoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterType>({});

  const debouncedSearch = useDebounce(search, 300);

  const displayTitle = title || (selectedProfession === 'all' 
    ? `Professional Directory (${(2010).toLocaleString()})` 
    : `${professionTypes.find(p => p.value === selectedProfession)?.label} Directory`);
    
  const displayDescription = description || (selectedProfession === 'all'
    ? 'Browse approved professionals in the SeniorThrive network.'
    : `Browse approved ${professionTypes.find(p => p.value === selectedProfession)?.label.toLowerCase()} in the SeniorThrive network.`);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (selectedProfession === 'all') {
          // Load all professionals using actual profession names from database
          const allProfessions = ['Geriatric Care Manager', 'Other', 'Occupational Therapist', 'Physical Therapist', 'Social Worker', 'Financial Advisor', 'Home Care Agency', 'Elder Law Attorney'];
          const promises = allProfessions.map(profession => 
            supabase.functions.invoke('get-professional-directory', {
              body: { profession, search: debouncedSearch, limit: 50, offset: 0 },
            })
          );
          
          const results = await Promise.all(promises);
          const allEntries = results.flatMap((result, index) => {
            if (result.error) {
              console.error(`Failed to load ${allProfessions[index]} directory:`, result.error);
              return [];
            }
            return (result.data as any)?.professionals ?? [];
          });
          
          setEntries(allEntries);
        } else {
          const { data, error } = await supabase.functions.invoke('get-professional-directory', {
            body: { profession: selectedProfession, search: debouncedSearch, limit: 50, offset: 0 },
          });
          if (error) throw error;
          setEntries((data as any)?.professionals ?? []);
        }
      } catch (e) {
        console.error('Failed to load directory', e);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [debouncedSearch, selectedProfession]);

  const handleProfessionChange = (value: string) => {
    setSelectedProfession(value);
    if (value === 'all') {
      searchParams.delete('type');
    } else {
      searchParams.set('type', value);
    }
    setSearchParams(searchParams);
  };

  const handleFiltersChange = (newFilters: FilterType) => {
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
  };

  const isCaregiversPage = selectedProfession === 'Independent Caregiver';

  const count = entries.length;

  return (
    <main className="container mx-auto px-4 py-10">
      <header className="mb-8">
        <h1 className="text-3xl font-bold tracking-tight">
          {displayTitle}
        </h1>
        <p className="text-muted-foreground mt-2">
          {displayDescription}
        </p>
      </header>

      <section className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by name or company"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search directory"
            />
          </div>
          {professionType === 'all' && (
            <div className="w-full sm:w-64">
              <Select value={selectedProfession} onValueChange={handleProfessionChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select profession" />
                </SelectTrigger>
                <SelectContent>
                  {professionTypes.map(profession => (
                    <SelectItem key={profession.value} value={profession.value}>
                      {profession.label}
                      {profession.count && ` (${profession.count.toLocaleString()})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Show caregiver filters only for Independent Caregivers */}
        {isCaregiversPage && (
          <CaregiverFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={clearFilters}
            isVisible={showFilters}
            onToggleVisibility={() => setShowFilters(!showFilters)}
          />
        )}
      </section>

      <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {loading && (
          <div className="col-span-full text-center text-muted-foreground">Loading directory…</div>
        )}
        {!loading && count === 0 && (
          <div className="col-span-full text-center text-muted-foreground">
            {selectedProfession === 'all' 
              ? "We're building our professional directory across the nation, stay tuned."
              : `We're building our ${professionTypes.find(p => p.value === selectedProfession)?.label.toLowerCase()} directory across the nation, stay tuned.`
            }
          </div>
        )}
        {!loading && entries.map((entry) => (
          <article key={entry.id} className="h-full">
            <ProfessionalCard professional={entry} />
          </article>
        ))}
      </section>

      <aside className="sr-only">
        <p>Public directory of professionals approved by SeniorThrive.</p>
      </aside>
    </main>
  );
}

function useDebounce<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default UnifiedProfessionalDirectory;